<template>
  <!-- 线上 -->
  <div v-if="formData" class="main-container">
    <Form ref="form" class="page-form" :model="formData" :label-width="120">
      <div class="form-title">基本信息</div>

      <FormItem label="报表类型" style="width: 300px">
        <span>{{ reportTypeLabel }}</span>
      </FormItem>
      <FormItem prop="settlementDate" label="结算日期" :rules="$units.formRule.required('请选择')">
        <template #label>
          <Poptip trigger="hover" word-wrap width="200" content="系统会根据您选择的结算周期时间判断上一自然季度的active状态">
            结算日期
            <Icon size="16" type="ios-help-circle-outline" />
          </Poptip>
        </template>
        <DatePicker
          type="date"
          format="yyyy-MM-dd"
          placeholder="选择日期"
          :editable="false"
          :value="formData.settlementDate"
          @on-change="$set(formData, 'settlementDate', $event)"
        ></DatePicker>
      </FormItem>
      <FormItem prop="reportName" label="结算报表名称" :rules="$units.formRule.required('请输入', 'blur')">
        <Input v-model="formData.reportName" placeholder="请输入" />
      </FormItem>
      <div class="form-title">待结算数据</div>
      <div class="search-box min ivu-form">
        <div class="input-item">
          <span class="label">保单号</span>
          <Input v-model="searchParams.policyNo" placeholder="请输入" clearable />
        </div>
        <div class="input-item" style="width: calc(300px + 2em)">
          <span class="label">佣金项类型</span>
          <Select v-model="searchParams.commissionTypeList" :max-tag-count="1" multiple clearable transfer>
            <template v-if="reportType === 'extra'">
              <Option value="1">周边收支</Option>
              <Option value="2">佣金调整</Option>
            </template>
            <template v-else>
              <Option value="1">基础销售佣金</Option>
              <Option value="11">个人Bonus</Option>
              <Option value="12">管理津贴</Option>
              <Option value="13">推荐奖</Option>
              <Option value="14">辅导奖</Option>
              <Option value="15">育成奖</Option>
            </template>
          </Select>
        </div>
        <div v-if="reportType === 'basic'" class="input-item">
          <span class="label">特殊标记单</span>
          <Select v-model="searchParams.isReferral" transfer>
            <Option value="999">全部</Option>
            <Option value="1">是</Option>
            <Option value="0">否</Option>
          </Select>
        </div>
        
        <div v-if="reportType === 'basic'" class="input-item">
          <span class="label">是否观察中</span>
          <Select v-model="searchParams.isObservation" transfer clearable>
            <Option value="1">是</Option>
            <Option value="0">否</Option>
          </Select>
        </div>
        <div class="btn-item">
          <Button type="primary" @click="searchItem">查询</Button>
        </div>
      </div>
      <div class="table-box">
        <loading :isShow="listLoading" />

        <div class="toolbox clearfix">
          <div class="statistics">
            <b>
              <span>汇总数据</span>
              <span>保单件数:{{ statistics.policyNum || 0 }}</span>
            </b>
          </div>
        </div>
        <div class="toolbox clearfix">
          <div class="statistics fl">
            <div class="fl">
              <Checkbox v-model="isAll" :disabled="!tableData || (tableData && !tableData.length)" @on-change="selectAll">全部</Checkbox>
            </div>
          </div>
        </div>
        <Table
          ref="selection"
          :columns="reportType === 'extra' ? extraColumns : columns"
          :data="tableData"
          @on-select="onSelect"
          @on-select-cancel="onSelectCancel"
          @on-select-all="onSelect"
          @on-select-all-cancel="onSelectAllCancel"
        >
          <template slot-scope="{ row }" slot="commissionType">
            <span>{{ commissionTypeMap[row.commissionType] }}</span>
            <Tag v-if="(row.commissionType == 15 || row.commissionType == 16) && row.isObservation === '1'" class="commission-tag" color="warning">观察</Tag>
          </template>
          <template slot-scope="{ row }" slot="action">
            <span v-if="reportType === 'basic' && row.isReferral === '1' && row.commissionType === '1'" class="table-btn" @click="editItem(row)">修改费率</span>
            <span class="table-btn red" @click="delItem(row)">删除</span>
          </template>
        </Table>
        <Page
          :total="total"
          :current="pageNum"
          :page-size="pageSize"
          show-total
          @on-change="getList"
          :page-size-opts="[10, 20, 30, 50, 100]"
          style="text-align: center"
        />
      </div>
    </Form>

    <div class="bottom-btn">
      <Button type="primary" ghost @click="back($route.query.backUrl)">返回</Button>
      <Button type="primary" :disabled="!canNext" ghost @click="selData">选择待结算数据</Button>
      <Button type="primary" ghost @click="delItem()">批量删除</Button>
      <Button type="primary" :disabled="!canNext || !tableData.length" @click="next">汇总与计算</Button>
    </div>
    <selPolicyModal ref="selPolicyModalRef" @close="getList"></selPolicyModal>
    <editRateModal ref="editRateModalRef" @success="getList"></editRateModal>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { common, pageTool } from '@/api/mixins';
import { config } from './step1.config.js';
import selPolicyModal from './components/selPolicyModal.vue';
import editRateModal from './components/editRateModal.vue';

export default {
  mixins: [common, config, pageTool],
  props: {
    id: {
      type: String,
      default: ''
    },
    settlementStatus: {
      type: String,
      default: ''
    },
    reportType: {
      type: String,
      default: 'basic'
    }
  },
  components: {
    selPolicyModal,
    editRateModal
  },
  computed: {
    canNext() {
      return this.formData.settlementDate && this.formData.reportName;
    },
    reportTypeLabel() {
      return this.reportType === 'extra' ? '周边收支与佣金调整' : '基本法';
    },
    apiPrefix() {
      return this.reportType === 'extra' ? '/buz/ifaExtraSettlementReport' : '/buz/ifaSettlementReport';
    }
  },

  data() {
    const dic = JSON.parse(localStorage.dic);
    return {
      commissionTypeMap: {
        1: '基础销售佣金',
        11: '个人Bonus',
        12: '管理津贴',
        13: '推荐奖',
        14: '辅导奖',
        15: '育成奖',
        16: '育成奖'
      },
      searchParams: {
        policyNo: '',
        commissionTypeList: [],
        isReferral: '999'
      },
      visible: false,
      loading: false,
      formData: {
        id: '',
        settlementDate: '',
        reportName: ''
      },
      statistics: {},
      isAll: false,
      selectArr: [],
      selectedIdsObj: {}
    };
  },
  created() {
    this.getColumns();
    if (this.id) {
      this.getDetailData(this.id);
      this.initListAjax(this.apiPrefix + '/querySettlementPage');
    } else {
      this.formData = {};
    }
  },
  methods: {
    async searchItem() {
      if (!this.id) {
        await this.save();
        this.initListAjax(this.apiPrefix + '/querySettlementPage');
      } else {
        this.searchAjax();
      }
    },
    getSelectedProductIds() {
      const selectedIdsObj = {};
      this.selectArr.forEach(p => {
        selectedIdsObj[p.id] = 1;
      });
      this.selectedIdsObj = selectedIdsObj;
    },
    listCallback(res) {
      this.getSelectedProductIds();
      if (res.data && res.data.length) {
        // this.isAll = true;
      } else {
        this.isAll = false;
      }
      res.data.forEach(item => {
        if (this.selectedIdsObj[item.id]) {
          item._checked = true;
        }
        if (this.isAll) {
          item._checked = true;
        }
      });
    },
    selectAll(status) {
      const tableData = JSON.parse(JSON.stringify(this.tableData));
      tableData.forEach(item => {
        item._checked = status;
      });
      this._searchAllParams = JSON.parse(JSON.stringify(this.searchParams));
      this.tableData = tableData;
      this.selectArr = tableData.filter(item => item._checked);
    },
    // 列表选中
    onSelect(selectArr) {
      selectArr.forEach(item => {
        this.selectPush(item);
      });
    },
    // 列表单个取消选中
    onSelectCancel(selectArr, row) {
      this.selectDel(row);
      this.isAll = false;
    },
    // 去掉选择数据
    selectDel(data) {
      this.selectArr.forEach((item, idx) => {
        if (item.id == data.id) {
          this.selectArr.splice(idx, 1);
        }
      });
      console.log('selectArr-------------', this.selectArr);
    },
    // 列表全选取消
    onSelectAllCancel() {
      this.tableData.forEach(item => {
        this.selectDel(item);
      });
      this.isAll = false;
    },
    // 添加选择数据
    selectPush(data) {
      const has = this.selectArr.some(item => item.id == data.id);
      if (!has) {
        this.selectArr.push(data);
        if (this.selectArr.length == this.total) {
          this.isAll = true;
        } else {
          this.isAll = false;
        }
      }
    },
    setListParams(params) {
      if (params.commissionTypeList && params.commissionTypeList.includes('15')) {
        params.commissionTypeList.push('16');
      }
      if (params.isReferral === '999') {
        delete params.isReferral;
      }
      if (this.reportType === 'extra') {
        params.extraSettleId = this.id;
      } else {
        params.settleId = this.id;
      }
      this.getStatistics();
    },
    async next() {
      await this.save();
      if (this.reportType === 'extra') {
        this.$notice.inquiry('是否进行汇总与计算?').then(() => {
          this.$emit('next', this.formData.reportName, this.id);
        });
        return;
      }
      this.$notice.inquiry('是否进行汇总与计算?').then(async () => {
        this.$http({
          url: this.apiPrefix + '/summary',
          type: 'GET',
          data: {
            id: this.id
          }
        }).then(res => {
          this.$notice.success('操作成功');
          this.$emit('next', this.formData.reportName, this.id);
        });
      });
    },
    // 选择待结算数据
    async selData() {
      await this.save();
      console.log('formData-------------', this.formData);
      this.$refs.selPolicyModalRef.open(this.formData, this.reportType);
    },
    // 修改费率
    editItem(row) {
      this.$refs.editRateModalRef.open(row);
    },
    // 批量删除
    delItem(row = null) {
      if (!row && this.selectArr.length === 0 && !this.isAll) {
        this.$notice.error('请选择要删除的数据项');
        return;
      }
      this.$notice.inquiry(row ? '请确认是否删除选中数据项？' : '请确认是否删除选中数据项？').then(async () => {
        let params = {
          isAll: this.isAll ? '1' : '0',
          ...(this.reportType === 'extra' ? { extraSettleId: this.id } : { settleId: this.id }),
          ids: row ? [row.id] : this.selectArr.map(item => item.id),
          ...this.searchParams
        };
        params = JSON.parse(JSON.stringify(params));
        if (params.commissionTypeList && params.commissionTypeList.includes('15')) {
          params.commissionTypeList.push('16');
        }
        this.$http({
          url: this.apiPrefix + '/batchDel',
          type: 'POST',
          data: params
        }).then(res => {
          this.$notice.success('操作成功');
          this.pageNum = 1;
          this.getList();
        });
      });
    },
    async save() {
      return new Promise((resolve, reject) => {
        this.$refs.form.validate((valid, obj) => {
          console.log('valid-------------', valid);
          if (!valid) {
            reject();
            return;
          }
          this.$http({
            url: this.apiPrefix + '/saveChange',
            type: 'POST',
            data: this.formData
          }).then(res => {
            let noId = !this.id;
            this.id = res.data.id;
            this.formData.id = res.data.id;
            resolve();
            noId && this.initListAjax(this.apiPrefix + '/querySettlementPage');
          });
        });
      });
    },

    getStatistics() {
      this.$http({
        url: this.apiPrefix + '/statistics',
        type: 'GET',
        data: {
          id: this.id
        }
      })
        .then(res => {
          this.statistics = res.data;
        })
        .finally(() => {
          this.$notice.hideLoading();
        });
    },
    getDetailData() {
      this.$notice.showLoading();
      this.$http({
        url: this.apiPrefix + '/read/detail',
        type: 'GET',
        data: {
          id: this.id
        }
      })
        .then(res => {
          this.formData = res.data;
        })
        .finally(() => {
          this.$notice.hideLoading();
        });
    }
  }
};
</script>
<style lang="scss" scoped>
.commission-tag {
  height: 18px;
  line-height: 18px;
  padding: 0 5px;
  border-radius: 4px;
  font-size: 12px;
  color: #fff;
  z-index: 100;
  transform: translateX(10px);
}

.main-container {
  padding-bottom: 100px;

  /deep/ .tip-icon {
    margin-left: 3px;
    width: 15px;
    height: 15px;
    border-radius: 15px;
    background: transparent;
    border: 1px solid #ccc;
    color: #ccc;
    display: inline-block;
    text-align: center;
    line-height: 14px;
    font-size: 14px;
    cursor: pointer;
    top: 50%;
  }
  .toolbox {
    margin-bottom: 10px;
  }
  /deep/ .ivu-form-item-label {
    &::after {
      content: ':';
    }
  }
  .statistics {
    span {
      margin-right: 10px;
    }
  }
}

.bottom-btn {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 70px;
  background-color: #fff;
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid #ccc;

  .ivu-btn {
    width: 140px;
    height: 35px;
  }

  .ivu-btn + .ivu-btn {
    margin-left: 20px;
  }
}

/deep/ .ivu-input-wrapper-disabled .ivu-input-group-append {
  border: none;
  background-color: #f4f7fa;
}
</style>
